import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { Inspector } from '@react-email/editor/ui';
import { useCurrentEditor } from '@tiptap/react';

import { useInspectorPresets } from '../../hooks/useInspectorPresets';
import { BRAND_PRESET_COLORS, withCustomColors } from '../../utils/inspectorColors';
import { closestPresetValue } from '../../utils/inspectorPresets';
import { effectiveStyleValue } from '../../utils/effectiveStyle';
import { readInlineStyle, writeInlineStyle } from '../../utils/inlineTextStyle';
import { AlignRow } from './AlignRow';
import { ColorSwatchRow } from './ColorSwatchRow';
import { FormatRow } from './FormatRow';
import { InspectorSection } from './InspectorSection';
import { PresetRow } from './PresetRow';

interface Props {
	customColors: string[];
	onCustomColorUsed: (hex: string) => void;
}

// Colour and size write an inline mark: the Inspector's own text `setStyle`
// restyles the whole parent block.
export const TextProperties = ({ customColors, onCustomColorUsed }: Props) => {
	const tMipManage = useTranslationMipManage();
	const presets = useInspectorPresets();
	const { editor } = useCurrentEditor();

	return (
		<Inspector.Text>
			{(ctx) => {
				if (!editor) {
					return null;
				}
				const presetColors = withCustomColors(
					ctx.presetColors.length > 0 ? ctx.presetColors : BRAND_PRESET_COLORS,
					customColors
				);
				const value = (property: 'color' | 'fontSize' | 'lineHeight') =>
					readInlineStyle(editor, property) ??
					ctx.getStyle(property) ??
					(property === 'lineHeight' ? undefined : effectiveStyleValue(editor, property));

				return (
					<>
						<InspectorSection
							title={tMipManage('email_templates.editor.inspector.typography', {
								defaultValue: 'Typography',
							})}
						>
							<ColorSwatchRow
								label={tMipManage('email_templates.editor.inspector.color', {
									defaultValue: 'Color',
								})}
								value={value('color')}
								presetColors={presetColors}
								onChange={(next) => writeInlineStyle(editor, 'color', next)}
								onCustomColorUsed={onCustomColorUsed}
							/>
							<PresetRow
								label={tMipManage('email_templates.editor.inspector.font_size', {
									defaultValue: 'Font size',
								})}
								options={presets.fontSize}
								value={closestPresetValue(presets.fontSize, value('fontSize'))}
								onChange={(next) => writeInlineStyle(editor, 'fontSize', next)}
							/>
							<PresetRow
								label={tMipManage('email_templates.editor.inspector.line_height', {
									defaultValue: 'Line height',
								})}
								options={presets.lineHeight}
								value={closestPresetValue(presets.lineHeight, value('lineHeight'))}
								onChange={(next) => writeInlineStyle(editor, 'lineHeight', next)}
							/>
							<FormatRow marks={ctx.marks} onToggle={ctx.toggleMark} />
							<AlignRow value={ctx.alignment} onChange={ctx.setAlignment} />
						</InspectorSection>

						{ctx.isLinkActive && (
							<InspectorSection
								title={tMipManage('email_templates.body_editor.link', {
									defaultValue: 'Link',
								})}
							>
								<div data-re-inspector-prop-row="">
									<label data-re-inspector-label="">
										{tMipManage('email_templates.editor.inspector.url', {
											defaultValue: 'URL',
										})}
									</label>
									<input
										data-re-inspector-input=""
										type="text"
										readOnly
										value={ctx.linkHref}
										title={ctx.linkHref}
										aria-label={tMipManage(
											'email_templates.editor.inspector.url',
											{ defaultValue: 'URL' }
										)}
									/>
								</div>
								<ColorSwatchRow
									label={tMipManage('email_templates.editor.inspector.color', {
										defaultValue: 'Color',
									})}
									value={ctx.linkColor}
									presetColors={presetColors}
									onChange={ctx.setLinkColor}
									onCustomColorUsed={onCustomColorUsed}
								/>
							</InspectorSection>
						)}
					</>
				);
			}}
		</Inspector.Text>
	);
};
